import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Search,
  RotateCcw,
  Download,
  Loader,
  X,
  AlertTriangle,
  FileSearch,
  Inbox,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
} from 'lucide-react';
import '../data-ingestion/data-ingestion.css';
import './uploaded-docs-log.css';
import UploadedDocsLogService, {
  TransactionType,
  UserOption,
  UploadedDocRow,
  SearchResponse,
  LookupsResponse,
  UsersSearchResponse,
  SearchEnvelope,
  SignedUrlResponse,
} from '../../_services/UploadedDocsLogService';

type Preset = 'today' | '7d' | '30d' | 'thisMonth' | 'custom';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function presetRange(preset: Preset): { from: string; to: string } | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const to = toIsoDate(today);
  if (preset === 'today') return { from: to, to };
  if (preset === '7d') {
    const d = new Date(today);
    d.setDate(d.getDate() - 6);
    return { from: toIsoDate(d), to };
  }
  if (preset === '30d') {
    const d = new Date(today);
    d.setDate(d.getDate() - 29);
    return { from: toIsoDate(d), to };
  }
  if (preset === 'thisMonth') {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: toIsoDate(first), to };
  }
  return null;
}

function basename(p: string | null): string | null {
  if (!p) return null;
  const cleaned = String(p).replace(/[\\/]+$/, '');
  const parts = cleaned.split(/[\\/]/);
  return parts[parts.length - 1] || null;
}

function formatDateTime(value: string | null): { date: string; time: string } {
  if (!value) return { date: '—', time: '' };
  const d = new Date(value.replace(' ', 'T'));
  if (isNaN(d.getTime())) return { date: String(value), time: '' };
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return { date, time };
}

const UploadedDocsLogPage: React.FC = () => {
  // Filter state
  const [preset, setPreset] = useState<Preset>('custom');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [transactionTypeId, setTransactionTypeId] = useState<number | ''>('');
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);

  // Lookups
  const [transactionTypes, setTransactionTypes] = useState<TransactionType[]>([]);

  // User typeahead
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState<UserOption[]>([]);
  const [userResultsOpen, setUserResultsOpen] = useState(false);
  const [userSearching, setUserSearching] = useState(false);
  const userDebounceRef = useRef<number | null>(null);
  const userWrapRef = useRef<HTMLDivElement | null>(null);

  // Results state
  const [hasSearched, setHasSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [rows, setRows] = useState<UploadedDocRow[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load lookups once
  useEffect(() => {
    UploadedDocsLogService.getLookups()
      .then((res) => {
        const body = res.data as LookupsResponse;
        if (body?.success) setTransactionTypes(body.data.transactionTypes);
      })
      .catch((e: any) => {
        setError(e?.response?.data?.error?.message || e?.message || 'Failed to load filter options');
      });
  }, []);

  // Close user dropdown on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!userWrapRef.current) return;
      if (!userWrapRef.current.contains(e.target as Node)) setUserResultsOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Debounced user typeahead
  useEffect(() => {
    if (selectedUser) return; // user already picked
    if (userDebounceRef.current) window.clearTimeout(userDebounceRef.current);
    if (!userQuery.trim()) {
      setUserResults([]);
      return;
    }
    setUserSearching(true);
    userDebounceRef.current = window.setTimeout(() => {
      UploadedDocsLogService.searchUsers(userQuery.trim())
        .then((res) => {
          const body = res.data as UsersSearchResponse;
          if (body?.success) setUserResults(body.data.users);
        })
        .catch(() => setUserResults([]))
        .finally(() => setUserSearching(false));
    }, 250);
    return () => {
      if (userDebounceRef.current) window.clearTimeout(userDebounceRef.current);
    };
  }, [userQuery, selectedUser]);

  const onPresetClick = (p: Preset) => {
    setPreset(p);
    if (p === 'custom') return;
    const range = presetRange(p);
    if (range) {
      setFromDate(range.from);
      setToDate(range.to);
    }
  };

  const onClear = () => {
    setPreset('custom');
    setFromDate('');
    setToDate('');
    setTransactionTypeId('');
    setSelectedUser(null);
    setUserQuery('');
    setUserResults([]);
    setError(null);
    setRows([]);
    setHasSearched(false);
    setPage(1);
    setTotalPages(1);
    setTotalRecords(0);
  };

  const runSearch = async (targetPage = 1, targetSize = pageSize) => {
    setSearching(true);
    setError(null);
    try {
      const res = await UploadedDocsLogService.search({
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        transactionTypeId: transactionTypeId === '' ? null : Number(transactionTypeId),
        userId: selectedUser?.id ?? null,
        page: targetPage,
        pageSize: targetSize,
      });
      const body = res.data as SearchEnvelope;
      if (body?.success) {
        const payload: SearchResponse = body.data;
        setRows(payload.rows);
        setPage(payload.pagination.page);
        setPageSize(payload.pagination.pageSize);
        setTotalPages(payload.pagination.totalPages);
        setTotalRecords(payload.pagination.totalRecords);
        setHasSearched(true);
      } else {
        setError('Search failed');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const onSearch = () => {
    setPage(1);
    runSearch(1, pageSize);
  };

  const onChangePage = (next: number) => {
    if (next < 1 || next > totalPages || next === page) return;
    runSearch(next, pageSize);
  };

  const onChangePageSize = (next: number) => {
    runSearch(1, next);
  };

  const onDownload = async (row: UploadedDocRow) => {
    if (!row.hasDownload || downloadingId === row.id) return;
    setDownloadingId(row.id);
    setError(null);
    try {
      const res = await UploadedDocsLogService.getSignedUrl(row.id);
      const body = res.data as SignedUrlResponse;
      if (body?.success && body.data.url) {
        const a = document.createElement('a');
        a.href = body.data.url;
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        setError('Could not get download link');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  const showingFrom = useMemo(() => (totalRecords === 0 ? 0 : (page - 1) * pageSize + 1), [page, pageSize, totalRecords]);
  const showingTo = useMemo(() => Math.min(page * pageSize, totalRecords), [page, pageSize, totalRecords]);

  return (
    <div className="data-ingestion">
      <div className="di-header">
        <h1>Uploaded Documents Log</h1>
        <p>Search and download files uploaded across ingestion, import, and export transactions.</p>
      </div>

      {/* --- Filter card --- */}
      <div className="udl-filter-card">
        <div className="udl-filter-header">
          <span className="udl-filter-title">Filters</span>
          <div className="udl-presets">
            {([
              ['today', 'Today'],
              ['7d', 'Last 7 days'],
              ['30d', 'Last 30 days'],
              ['thisMonth', 'This month'],
              ['custom', 'Custom'],
            ] as Array<[Preset, string]>).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`udl-chip ${preset === key ? 'active' : ''}`}
                onClick={() => onPresetClick(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="udl-filter-grid">
          <div className="udl-field">
            <label htmlFor="udl-from">From date</label>
            <input
              id="udl-from"
              type="date"
              className="udl-input"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setPreset('custom'); }}
              max={toDate || undefined}
            />
          </div>
          <div className="udl-field">
            <label htmlFor="udl-to">To date</label>
            <input
              id="udl-to"
              type="date"
              className="udl-input"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setPreset('custom'); }}
              min={fromDate || undefined}
            />
          </div>
          <div className="udl-field">
            <label htmlFor="udl-type">Transaction type</label>
            <select
              id="udl-type"
              className="udl-select"
              value={transactionTypeId}
              onChange={(e) => setTransactionTypeId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">All types</option>
              {transactionTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="udl-field">
            <label htmlFor="udl-user">User</label>
            <div className="udl-typeahead-wrap" ref={userWrapRef}>
              <input
                id="udl-user"
                type="text"
                className="udl-input udl-typeahead-input"
                placeholder="Search by name or email…"
                value={selectedUser ? selectedUser.name : userQuery}
                onChange={(e) => {
                  if (selectedUser) setSelectedUser(null);
                  setUserQuery(e.target.value);
                  setUserResultsOpen(true);
                }}
                onFocus={() => { if (userResults.length > 0) setUserResultsOpen(true); }}
                autoComplete="off"
              />
              {(selectedUser || userQuery) && (
                <button
                  type="button"
                  className="udl-typeahead-clear"
                  onClick={() => { setSelectedUser(null); setUserQuery(''); setUserResults([]); setUserResultsOpen(false); }}
                  aria-label="Clear user"
                >
                  <X size={14} />
                </button>
              )}
              {userResultsOpen && !selectedUser && userQuery.trim() && (
                <div className="udl-typeahead-results">
                  {userSearching ? (
                    <div className="udl-typeahead-empty">Searching…</div>
                  ) : userResults.length === 0 ? (
                    <div className="udl-typeahead-empty">No users found</div>
                  ) : (
                    userResults.map((u) => (
                      <div
                        key={u.id}
                        className="udl-typeahead-item"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setSelectedUser(u);
                          setUserQuery('');
                          setUserResults([]);
                          setUserResultsOpen(false);
                        }}
                      >
                        {u.name}
                        {u.email && <span className="udl-typeahead-item-email">{u.email}</span>}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="udl-filter-actions">
            <button type="button" className="di-btn-secondary" onClick={onClear} disabled={searching}>
              <RotateCcw size={16} /> Clear
            </button>
            <button type="button" className="di-btn-primary" onClick={onSearch} disabled={searching}>
              {searching ? <Loader size={16} className="udl-spinner" /> : <Search size={16} />}
              {searching ? 'Searching…' : 'Search'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="udl-error-banner">
          <AlertTriangle size={16} />
          <span>{error}</span>
          <button
            type="button"
            className="di-error-close"
            onClick={() => setError(null)}
            aria-label="Dismiss error"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* --- Results card --- */}
      <div className="udl-results-card">
        <div className="udl-results-header">
          <div className="udl-results-title">
            Results
            {hasSearched && (
              <span className="udl-results-count">
                {totalRecords.toLocaleString()} {totalRecords === 1 ? 'record' : 'records'}
                {totalRecords > 0 && ` · showing ${showingFrom.toLocaleString()}–${showingTo.toLocaleString()}`}
              </span>
            )}
          </div>
        </div>

        {!hasSearched ? (
          <div className="udl-state">
            <FileSearch size={56} className="udl-state-icon" />
            <h3>Apply filters to begin</h3>
            <p>Choose a date range, transaction type, or user — then click <strong>Search</strong> to view uploaded documents.</p>
          </div>
        ) : searching ? (
          <div className="udl-state">
            <Loader size={40} className="udl-state-icon udl-spinner" />
            <h3>Loading documents…</h3>
          </div>
        ) : rows.length === 0 ? (
          <div className="udl-state">
            <Inbox size={56} className="udl-state-icon" />
            <h3>No documents found</h3>
            <p>Try widening the date range or removing a filter.</p>
          </div>
        ) : (
          <>
            <div className="udl-table-wrapper">
              <table className="udl-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>User</th>
                    <th>Transaction type</th>
                    <th>File name</th>
                    <th className="udl-cell-download">Download</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const dt = formatDateTime(row.transactionDate);
                    const fileName = basename(row.filePath) || row.name || '—';
                    return (
                      <tr key={row.id}>
                        <td className="udl-cell-date">
                          {dt.date}
                          {dt.time && <span className="udl-cell-time">{dt.time}</span>}
                        </td>
                        <td className="udl-cell-user">{row.transactionByUserName || '—'}</td>
                        <td>
                          {row.transactionTypeName
                            ? <span className="udl-type-pill">{row.transactionTypeName}</span>
                            : '—'}
                        </td>
                        <td className="udl-cell-filename" title={row.filePath || row.name || ''}>
                          {fileName}
                        </td>
                        <td className="udl-cell-download">
                          <button
                            type="button"
                            className={`udl-download-btn ${downloadingId === row.id ? 'loading' : ''}`}
                            onClick={() => onDownload(row)}
                            disabled={!row.hasDownload || downloadingId === row.id}
                            title={row.hasDownload ? 'Download file' : 'No file available'}
                            aria-label={row.hasDownload ? 'Download file' : 'No file available'}
                          >
                            {downloadingId === row.id
                              ? <Loader size={16} className="udl-spinner" />
                              : <Download size={16} />}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="udl-pagination">
              <div className="udl-pagination-info">
                <span>Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => onChangePageSize(Number(e.target.value))}
                  disabled={searching}
                >
                  {PAGE_SIZE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <span>
                  Showing {showingFrom.toLocaleString()}–{showingTo.toLocaleString()} of {totalRecords.toLocaleString()}
                </span>
              </div>
              <div className="udl-pagination-controls">
                <button
                  type="button"
                  className="udl-page-btn"
                  onClick={() => onChangePage(1)}
                  disabled={page <= 1 || searching}
                  aria-label="First page"
                >
                  <ChevronsLeft size={14} />
                </button>
                <button
                  type="button"
                  className="udl-page-btn"
                  onClick={() => onChangePage(page - 1)}
                  disabled={page <= 1 || searching}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="udl-page-indicator">Page {page} of {totalPages}</span>
                <button
                  type="button"
                  className="udl-page-btn"
                  onClick={() => onChangePage(page + 1)}
                  disabled={page >= totalPages || searching}
                  aria-label="Next page"
                >
                  <ChevronRight size={14} />
                </button>
                <button
                  type="button"
                  className="udl-page-btn"
                  onClick={() => onChangePage(totalPages)}
                  disabled={page >= totalPages || searching}
                  aria-label="Last page"
                >
                  <ChevronsRight size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UploadedDocsLogPage;
